import { useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useBackLayer } from "../backstack";
import { HIDDEN, PageShown } from "../pageShown";
import { ShellIcon, type ShellIconName } from "./ShellIcons";
import "../xa.css";

/**
 * Pictures and clips made to be looked at: 素材库, and 小爱's files on her page.
 *
 * 🔴 The owner, 2026-10-03: 「素材库的加载卡卡的」「素材库图片和video这样呈现你要
 * admin和cs怎么看？全被裁剪了挤一团要怎么看？」. Every card downloaded its whole
 * file as it was drawn — a hundred at once, every clip in full — to show a
 * square crop with three form fields squeezed under it. Now a card shows a
 * preview the API makes small (?w=, console/preview.go), fetched when the
 * card comes into view, the whole picture with nothing cut off; the file
 * itself comes when somebody opens it, in a viewer big enough to read a
 * price list, which is where its words are changed.
 */

/** The width a card asks for: a sharp screen's card. The API rounds it up to one of its sizes. */
export const TILE_W = 480;
/** The width the viewer asks for: a price list read on a laptop. */
export const VIEW_W = 1280;

// ------------------------------------------------------------ the previews

/** Fetched previews, by what they are of, for the page's life: going back to
 *  a filter or the next file shows them at once. A failure is kept too — a
 *  file with no preview will not grow one by being asked again. */
const kept = new Map<string, Promise<string>>();
/** Downloads waiting, and how many run: a page of a hundred cards asks a few at a time. */
const waiting: (() => void)[] = [];
let running = 0;
const AT_ONCE = 4;

function pump() {
  while (running < AT_ONCE && waiting.length > 0) {
    running++;
    waiting.shift()!();
  }
}

/** The object URL of a preview, fetched once for the page's life. */
export function previewURL(id: string, load: () => Promise<Blob>): Promise<string> {
  let p = kept.get(id);
  if (!p) {
    p = new Promise<string>((resolve, reject) => {
      waiting.push(() => {
        load()
          .then((b) => resolve(URL.createObjectURL(b)))
          .catch(reject)
          .finally(() => {
            running--;
            pump();
          });
      });
      pump();
    });
    kept.set(id, p);
  }
  return p;
}

/** A preview fetched when its card comes near the screen. id names the file
 *  and the size; a new id (换一张) fetches again. */
export function usePreview(id: string | null, load: () => Promise<Blob>) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [near, setNear] = useState(false);
  const [got, setGot] = useState<{ id: string; url: string | null; failed: boolean } | null>(null);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    const el = ref.current;
    if (near || !el) return;
    if (typeof IntersectionObserver === "undefined") {
      setNear(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin: "300px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [near]);

  useEffect(() => {
    if (!near || !id) return;
    let live = true;
    previewURL(id, () => loadRef.current())
      .then((url) => live && setGot({ id, url, failed: false }))
      .catch(() => live && setGot({ id, url: null, failed: true }));
    return () => {
      live = false;
    };
  }, [near, id]);

  const mine = got && got.id === id ? got : null;
  return { ref, url: mine?.url ?? null, failed: !!mine?.failed };
}

// ------------------------------------------------------------------ a card

export type FileKind = "图片" | "影片" | "语音" | "档案";

export function kindOfMime(mime: string): FileKind {
  if (mime.startsWith("image/")) return "图片";
  if (mime.startsWith("video/")) return "影片";
  if (mime.startsWith("audio/")) return "语音";
  return "档案";
}

/**
 * One file as a card: the whole picture, its name, and one line under it.
 * The card is the button — it opens the viewer.
 */
export function Tile({
  previewId,
  loadPreview,
  kind,
  title,
  sub,
  subWarn,
  flag,
  dim,
  onOpen,
}: {
  /** null: no preview to ask for (a voice note). */
  previewId: string | null;
  loadPreview: () => Promise<Blob>;
  kind: FileKind;
  title: string;
  sub?: ReactNode;
  /** The line under it is something to do, not a description. */
  subWarn?: boolean;
  /** One word on the picture: 已过期. */
  flag?: string;
  dim?: boolean;
  onOpen: () => void;
}) {
  const p = usePreview(previewId, loadPreview);
  return (
    <button type="button" className={`xa-tile${dim ? " dim" : ""}`} onClick={onOpen} title={title}>
      <div className={`xa-thumb${kind === "语音" ? " voice" : ""}`} ref={p.ref}>
        {p.url ? (
          <img src={p.url} alt="" decoding="async" />
        ) : kind === "语音" ? (
          <span className="xa-wave" aria-hidden="true">
            {WAVE.map((h, i) => (
              <i key={i} style={{ height: h }} />
            ))}
          </span>
        ) : (
          <span className="xa-thumb-ph">{p.failed ? kind : ""}</span>
        )}
        {kind === "影片" && (
          <span className="xa-play" aria-hidden="true">
            <ShellIcon name="play" size={14} />
          </span>
        )}
        {flag && <span className="xa-flag">{flag}</span>}
      </div>
      <span className="xa-tile-t">
        <b>
          <ShellIcon name={KIND_ICON[kind]} size={13} />
          {/* Its own box, so a long name ends in 「…」: cut inside the flex row it stopped mid-word with none. */}
          <span className="xa-tile-n">{title}</span>
        </b>
        {sub && <span className={subWarn ? "warn" : ""}>{sub}</span>}
      </span>
    </button>
  );
}

/** A voice note's bars, as WhatsApp draws one (the prototype's AiWave). */
const WAVE = [6, 11, 15, 9, 17, 12, 7, 14, 19, 11, 6, 13];
const KIND_ICON: Record<FileKind, ShellIconName> = { 图片: "image", 影片: "video", 语音: "mic", 档案: "doc" };

// -------------------------------------------------------------- the viewer

/**
 * A file opened: big on the left, its words on the right (one column on a
 * phone). ← → and Esc on a keyboard; the arrows on the picture otherwise.
 */
export function Viewer({
  title,
  pos,
  media,
  side,
  onPrev,
  onNext,
  onClose,
}: {
  title: string;
  /** 「3 / 12」 */
  pos?: string;
  media: ReactNode;
  side: ReactNode;
  onPrev?: () => void;
  onNext?: () => void;
  onClose: () => void;
}) {
  const close = useRef<HTMLButtonElement>(null);
  // A frame later, once the viewer has been laid out with the page's own frame: focused as it mounted, it laid the
  // page out on the spot (33 ms on 素材库's 144 files, 10/28). Not scrolled to: it is in sight.
  useEffect(() => {
    const r = window.requestAnimationFrame(() => close.current?.focus({ preventScroll: true }));
    return () => window.cancelAnimationFrame(r);
  }, []);
  // It goes with its page, as a drawer does (pageShown.ts): another page on screen hides it, its keys are not its own
  // then, and a phone's back closes it.
  const shown = useContext(PageShown);
  const shut = useRef(onClose);
  shut.current = onClose;
  useBackLayer(true, () => shut.current());
  useEffect(() => {
    if (!shown) return;
    const onKey = (e: KeyboardEvent) => {
      const typing = e.target instanceof HTMLElement && /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName);
      if (e.key === "Escape") onClose();
      else if (!typing && e.key === "ArrowLeft" && onPrev) onPrev();
      else if (!typing && e.key === "ArrowRight" && onNext) onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onPrev, onNext, onClose, shown]);
  // Each file is a new picture sliding in from the right and its words fading in (prototype ai.css ai-slide, fade).
  const at = pos ?? title;

  return createPortal(
    <div className="xa-vw-scrim" style={shown ? undefined : HIDDEN} onClick={onClose}>
      <div className="xa-vw" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="xa-vw-stage">
          <div className="xa-vw-media" key={`m${at}`}>
            {media}
          </div>
          {onPrev && (
            <button type="button" className="xa-vw-nav prev" aria-label="上一个" onClick={onPrev}>
              <ShellIcon name="left" size={22} />
            </button>
          )}
          {onNext && (
            <button type="button" className="xa-vw-nav next" aria-label="下一个" onClick={onNext}>
              <ShellIcon name="right" size={22} />
            </button>
          )}
        </div>
        <div className="xa-vw-side">
          <div className="xa-vw-h">
            <h4>{title}</h4>
            {pos && <span className="xa-vw-pos lz-n">{pos}</span>}
            <button ref={close} type="button" className="xa-vw-x" aria-label="关掉" onClick={onClose}>
              <ShellIcon name="x" size={18} />
            </button>
          </div>
          <div className="xa-vw-body" key={`b${at}`}>
            {side}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** The viewer's wave, longer (prototype ai.js AI_WAVE). */
const WAVE_BIG = [...WAVE, 16, 9, 12, 15, 8, 10, 13, 6, 9, 14];

/** 0:07 */
const clockOf = (s: number) => (Number.isFinite(s) && s >= 0 ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}` : "");

/**
 * A voice note in the viewer, played as the prototype plays one (ai.js AiVwMedia): a big play button, the wave hopping
 * while it plays, how far it is of how long, and a line under it running to the end — the line is the file's own
 * position, drawn each frame as a transform, and a press on it jumps there.
 */
function VoicePlayer({ src }: { src: string }) {
  const audio = useRef<HTMLAudioElement>(null);
  const fill = useRef<HTMLElement>(null);
  const [playing, setPlaying] = useState(false);
  const [dur, setDur] = useState(0);
  const [now, setNow] = useState(0);
  const draw = () => {
    const a = audio.current;
    const f = fill.current;
    if (a && f && a.duration > 0 && Number.isFinite(a.duration)) f.style.transform = `scaleX(${Math.min(1, a.currentTime / a.duration)})`;
  };
  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    const tick = () => {
      draw();
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [playing]);
  const toggle = () => {
    const a = audio.current;
    if (!a) return;
    if (a.paused) void a.play().catch(() => undefined);
    else a.pause();
  };
  const jump = (frac: number) => {
    const a = audio.current;
    if (!a || !(a.duration > 0) || !Number.isFinite(a.duration)) return;
    a.currentTime = Math.max(0, Math.min(1, frac)) * a.duration;
    setNow(a.currentTime);
    draw();
  };
  return (
    <div className="xa-vw-voice">
      <audio
        ref={audio}
        src={src}
        autoPlay
        preload="auto"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onLoadedMetadata={(e) => setDur(e.currentTarget.duration)}
        onDurationChange={(e) => setDur(e.currentTarget.duration)}
        onTimeUpdate={(e) => setNow(e.currentTarget.currentTime)}
      />
      <button type="button" className="xa-vw-play" aria-label={playing ? "停" : "听"} onClick={toggle}>
        <ShellIcon name={playing ? "pause" : "play"} size={26} />
      </button>
      <span className={`xa-wave big${playing ? " on" : ""}`} aria-hidden="true">
        {WAVE_BIG.map((h, i) => (
          <i key={i} style={{ height: h * 2.6, ["--i" as string]: i }} />
        ))}
      </span>
      <span className="xa-vw-time lz-n">
        {clockOf(now)}
        {Number.isFinite(dur) && dur > 0 ? ` / ${clockOf(dur)}` : ""}
      </span>
      <span
        className="xa-vw-prog"
        role="slider"
        tabIndex={0}
        aria-label="听到哪里"
        aria-valuemin={0}
        aria-valuemax={Number.isFinite(dur) ? Math.round(dur) : 0}
        aria-valuenow={Math.round(now)}
        onPointerDown={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          if (r.width > 0) jump((e.clientX - r.left) / r.width);
        }}
        onKeyDown={(e) => {
          const a = audio.current;
          if (!a || !(a.duration > 0)) return;
          if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
            e.preventDefault();
            e.stopPropagation();
            jump((a.currentTime + (e.key === "ArrowLeft" ? -3 : 3)) / a.duration);
          }
        }}
      >
        <i ref={fill} />
      </span>
    </div>
  );
}

/**
 * The file itself, in the viewer. A picture shows its big preview — sharp
 * enough to read, and the card's preview stands in until it comes; a tap
 * shows it at full size. A clip or a voice note is downloaded now, since
 * somebody opened it to play it, and let go when the viewer moves on.
 */
export function ViewerMedia({
  kind,
  smallId,
  bigId,
  loadSmall,
  loadBig,
  loadFile,
}: {
  kind: FileKind;
  smallId: string | null;
  bigId: string | null;
  loadSmall: () => Promise<Blob>;
  loadBig: () => Promise<Blob>;
  /** The whole file, for a clip or a voice note. */
  loadFile: () => Promise<Blob>;
}) {
  const [small, setSmall] = useState<string | null>(null);
  const [big, setBig] = useState<string | null>(null);
  const [file, setFile] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState(false);
  const loads = useRef({ loadSmall, loadBig, loadFile });
  loads.current = { loadSmall, loadBig, loadFile };

  useEffect(() => {
    let live = true;
    setSmall(null);
    setBig(null);
    setZoom(false);
    setFailed(false);
    if (smallId) previewURL(smallId, () => loads.current.loadSmall()).then((u) => live && setSmall(u), () => undefined);
    if (bigId) previewURL(bigId, () => loads.current.loadBig()).then((u) => live && setBig(u), () => live && kind === "图片" && setFailed(true));
    return () => {
      live = false;
    };
  }, [smallId, bigId, kind]);

  useEffect(() => {
    if (kind !== "影片" && kind !== "语音") return;
    let live = true;
    let obj: string | null = null;
    setFile(null);
    loads.current
      .loadFile()
      .then((b) => {
        if (!live) return;
        obj = URL.createObjectURL(b);
        setFile(obj);
      })
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
      // A clip is the whole file in memory: let it go with the viewer.
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [kind, bigId]);

  const poster = big || small || undefined;
  if (kind === "影片") {
    return (
      <>
        {file ? (
          <video key={file} className="xa-vw-vid" src={file} poster={poster} controls autoPlay playsInline />
        ) : poster ? (
          <img src={poster} alt="" className="xa-vw-poster" />
        ) : null}
        {!file && <span className="xa-vw-loading">{failed ? "影片打不开" : "影片下载中…"}</span>}
      </>
    );
  }
  if (kind === "语音") {
    return file ? <VoicePlayer key={file} src={file} /> : <span className="xa-vw-loading">{failed ? "语音打不开" : "语音下载中…"}</span>;
  }
  const src = big || small;
  return (
    <>
      {src && (
        <img
          src={src}
          alt=""
          className={`xa-vw-img${zoom ? " zoom" : ""}`}
          title={zoom ? "缩回去" : "看原尺寸"}
          onClick={() => setZoom(!zoom)}
        />
      )}
      {!big && <span className="xa-vw-loading">{failed ? "图片打不开" : "载入中…"}</span>}
    </>
  );
}
